/**
 * SocialCard — uniform dark card for one social post, regardless of platform.
 *
 * Renders our own design (not a platform iframe) using the backend-proxied
 * cover image. Click opens SocialModal with the full platform iframe inside.
 *
 * Goal: visual consistency across IG / TikTok / X. Card chrome, dimensions,
 * typography all match the rest of the hub's dark design. The platform's
 * iframe only appears INSIDE our modal for full interaction (playback,
 * comments, etc).
 */

import React, { useEffect, useState } from "react";
import { FaInstagram, FaTiktok, FaTwitter, FaHeart, FaComment, FaRetweet, FaImage } from "react-icons/fa";
import { formatViews, timeAgo } from "./shared";

export type SocialCardPlatform = "INSTAGRAM" | "TIKTOK" | "X";

export interface SocialCardData {
    id: string;
    platform: SocialCardPlatform;
    coverImageProxyUrl?: string | null;
    /** Fallback when proxy URL is null — directly the platform's CDN URL */
    fallbackImageUrl?: string;
    /** Tweet text — used for X cards where text IS the content */
    textBody?: string;
    caption?: string;
    authorDisplayName?: string;
    authorHandle?: string;
    when?: string;
    likeCount?: number;
    commentCount?: number;
    repostCount?: number;
    /** Aspect ratio hint for native sizing — defaults to 1:1 (square) when missing */
    aspectRatio?: number;
}

interface Props {
    data: SocialCardData;
    onClick: (data: SocialCardData) => void;
}

const PLATFORM_ICON: Record<SocialCardPlatform, React.ReactNode> = {
    INSTAGRAM: <FaInstagram />,
    TIKTOK: <FaTiktok />,
    X: <FaTwitter />,
};

export const SocialCard: React.FC<Props> = ({ data, onClick }) => {
    /*
     * Instagram path — use IG's official embed iframe instead of trying to
     * hotlink or proxy the image. IG's CDN is hostile to third-party image
     * hotlinking (signed-param expiry, aggressive referer checks, ToS
     * caching limits), but their `/p/<shortcode>/embed/` widget is the
     * sanctioned mechanism for displaying a post on an external site —
     * same mechanism the SocialModal already uses successfully. Inlining
     * it here makes the card render reliably with zero proxy/scraping
     * infra. `hidecaption=true` suppresses IG's own caption so our card
     * chrome below isn't duplicated; clicks inside the iframe behave
     * natively (open the post on instagram.com in a new tab via IG's own
     * handlers — we don't trigger the modal for IG since the iframe
     * already IS the embed).
     */
    if (data.platform === "INSTAGRAM") {
        return (
            <div
                className={`hcs-social-card hcs-social-card--${data.platform.toLowerCase()} hcs-social-card--embed`}
            >
                <div
                    className="hcs-social-card__media hcs-social-card__media--embed"
                    style={data.aspectRatio ? { aspectRatio: data.aspectRatio } : undefined}
                >
                    <iframe
                        src={`https://www.instagram.com/p/${encodeURIComponent(data.id)}/embed/?hidecaption=true`}
                        title={data.caption?.slice(0, 80) ?? "Instagram post"}
                        loading="lazy"
                        scrolling="no"
                        allow="encrypted-media"
                    />
                    {/*
                     * No platform badge on Instagram embeds — Instagram's own
                     * iframe renders the IG branding + creator profile pic +
                     * username in its top-left header. Overlaying our badge
                     * lands directly on top of the creator's profile pic.
                     */}
                </div>

                {/*
                 * No author / caption / stats chrome below the iframe.
                 * Instagram's embed already shows the username, caption,
                 * and like / comment counts inside the iframe — rendering
                 * the same metadata in a second strip below was a visible
                 * duplicate (and the only place our chrome appeared in
                 * the wrong theme since IG's iframe is light-only).
                 */}
            </div>
        );
    }

    /*
     * onError fallback chain. The image src walks through preferences:
     *   0. GCS-proxied URL (most reliable when present)
     *   1. Platform CDN URL with no-referrer (works for IG/TT/X most of the time)
     *   2. Give up — render media-empty state, no broken-image icon
     * The walk advances only when the current src actually fails to load
     * (img.onerror). Reset on data change (new card slot reused for new post).
     */
    const candidates: string[] = [];
    if (data.coverImageProxyUrl) candidates.push(data.coverImageProxyUrl);
    if (data.fallbackImageUrl && data.fallbackImageUrl !== data.coverImageProxyUrl) {
        candidates.push(data.fallbackImageUrl);
    }
    const [srcIndex, setSrcIndex] = useState(0);
    useEffect(() => {
        setSrcIndex(0);
    }, [data.coverImageProxyUrl, data.fallbackImageUrl]);
    const imageSrc = candidates[srcIndex];
    const handleImgError = () => {
        if (srcIndex < candidates.length - 1) setSrcIndex(i => i + 1);
        else setSrcIndex(candidates.length); // past end → triggers empty-media render
    };
    const hasUsableImage = !!imageSrc && srcIndex < candidates.length;

    /*
     * Text-only mode: X tweet AND there's no usable image AND we have a
     * non-empty text body to render. If textBody is missing the card falls
     * through to the generic empty-media state instead of rendering an X
     * icon over an empty dark void.
     */
    const hasText = !!(data.textBody && data.textBody.trim().length > 0);
    const isTextOnly = data.platform === "X" && !hasUsableImage && hasText;

    /*
     * Distinguish "engagement count is zero" from "platform didn't return a
     * count" — `!= null` is the correct check. `||` collapses 0 into "hide
     * the row," which makes legitimately-zero engagement render as missing
     * data.
     */
    const showStats = data.likeCount != null || data.commentCount != null || data.repostCount != null;

    return (
        <button type="button" className={`hcs-social-card hcs-social-card--${data.platform.toLowerCase()}`}
            onClick={() => onClick(data)}>
            <div className="hcs-social-card__media" style={data.aspectRatio ? { aspectRatio: data.aspectRatio } : undefined}>
                {isTextOnly
                    ? <div className="hcs-social-card__text-only">
                        <FaTwitter className="hcs-social-card__text-only-icon" />
                        <div className="hcs-social-card__text-body">{data.textBody}</div>
                    </div>
                    : hasUsableImage
                    /*
                     * `referrerPolicy="no-referrer"` strips the Referer header so
                     * IG/TT/X CDNs serve the image (they 403 anything with a
                     * non-platform Referer). onError walks the candidates chain
                     * — proxy URL first, then platform CDN, then empty-state.
                     * Guarantees the user never sees a broken-image icon.
                     */
                        ? <img
                            key={`${data.id}-${srcIndex}`}
                            src={imageSrc}
                            alt={data.caption?.slice(0, 80) ?? "Post"}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={handleImgError}
                        />
                        : <div className="hcs-social-card__media-empty">
                            <FaImage />
                        </div>
                }
                <span className={`hcs-social-card__badge hcs-social-card__badge--${data.platform.toLowerCase()}`}>
                    {PLATFORM_ICON[data.platform]}
                </span>
            </div>

            {(data.caption || data.authorDisplayName) &&
                <div className="hcs-social-card__body">
                    {(data.authorDisplayName || data.when) &&
                        <div className="hcs-social-card__author">
                            {data.authorDisplayName && <span className="hcs-social-card__author-name">{data.authorDisplayName}</span>}
                            {data.when && <span className="hcs-social-card__when">{timeAgo(data.when)}</span>}
                        </div>
                    }
                    {data.caption && !isTextOnly &&
                        <div className="hcs-social-card__caption">{data.caption.slice(0, 120)}</div>
                    }
                    {showStats &&
                        <div className="hcs-social-card__stats">
                            {data.likeCount != null && <span><FaHeart /> {formatViews(data.likeCount) || "0"}</span>}
                            {data.commentCount != null && <span><FaComment /> {formatViews(data.commentCount) || "0"}</span>}
                            {data.repostCount != null && <span><FaRetweet /> {formatViews(data.repostCount) || "0"}</span>}
                        </div>
                    }
                </div>
            }
        </button>
    );
};
